// Tonk in VR: the join panel over the table, then — seated — a lap panel
// (grab bar to move it) with the lobby + hands option, big Draw / Take /
// Drop buttons, your hand as tappable cards, Spread / Hit / Discard, the
// scores, Leave, the rules and the leaderboard. Everything is a large poke /
// pinch-ray target: playable with bare hands. Table-local, floor origin.

import React, { useState } from 'react';
import { Text } from '@react-three/drei';
import { tableAction, handOf } from '../gameStore';
import { leaveTable } from '../kit/LobbyKit';
import { VRBtn, VRJoinPanel, VRLobbyBody, VRRulesPanel, VRLeaderboardPanel, SeatPanelMount, vrColors as colors, noRaycast, Surface } from '../kit/vrKit';
import { cardLabel, isRed } from '../cardFaces';
import { handTotal, HAND_OPTIONS, DEFAULT_HANDS, type TonkState, type TonkMove } from '../../../../supabase/functions/games/tonk.ts';
import { tonkName, isTurn, hitTargets, canSpread, handLine, spreadText, useSelection } from './tonkView';
import type { VRPanelProps } from '../types';

const CW = 0.07, CH = 0.1;

function VRCard({ card, x, y, enabled, selected, onPress }: { card: string; x: number; y: number; enabled: boolean; selected: boolean; onPress: () => void }) {
  const [hover, setHover] = useState(false);
  const lift = selected ? 0.018 : hover && enabled ? 0.006 : 0;
  return (
    <group position={[x, y + lift, 0.006]} name={`vr-tonk-card-${card}`}
      onPointerOver={(e) => { e.stopPropagation(); setHover(true); }} onPointerOut={() => setHover(false)}
      onClick={(e) => { e.stopPropagation(); if (enabled) onPress(); }}>
      {selected && <Surface width={CW + 0.012} height={CH + 0.012} color={colors.teal} r={0.009} z={-0.001} />}
      <Surface width={CW} height={CH} color={enabled || selected ? '#fbfaf6' : '#c9ccd0'} r={0.007} />
      <Text raycast={noRaycast} position={[0, 0, 0.003]} fontSize={0.028} color={isRed(card) ? '#c8102e' : '#16181d'}>{cardLabel(card)}</Text>
    </group>
  );
}

export function TonkVRPanel({ def, pid, worldId, entry, table, seat }: VRPanelProps) {
  const [rules, setRules] = useState(false);
  const [board, setBoard] = useState(false);
  const st = table.state;
  const g = st.g as TonkState | null;
  const hand = handOf(entry);
  const { sel, setSel, toggle } = useSelection(hand, g ? `${g.handNo}:${g.turn}:${g.phase}` : '');

  if (seat < 0) return <VRJoinPanel def={def} pid={pid} entry={entry} worldId={worldId} />;

  const W = 0.84, H = 0.54;
  const busy = entry.busy;
  const send = (move: TonkMove) => { setSel([]); void tableAction(pid, 'move', { move }); };
  const myTurn = isTurn(st, g, seat);
  const drawPhase = myTurn && g?.phase === 'draw';
  const playPhase = myTurn && g?.phase === 'play';
  const one = sel.length === 1 ? sel[0] : undefined;
  const targets = g && playPhase ? hitTargets(g, one) : [];
  const total = handTotal(hand);
  const T = H / 2;

  let body: React.ReactNode = null;
  if (st.phase === 'lobby') {
    const cur = Number(st.options?.hands) || DEFAULT_HANDS;
    body = <>
      <group position={[0, 0.06, 0]}><VRLobbyBody def={def} pid={pid} entry={entry} /></group>
      <Text raycast={noRaycast} position={[-0.33, -0.08, 0.004]} anchorX="left" fontSize={0.018} color={colors.text}>Hands</Text>
      {HAND_OPTIONS.map((n, k) => (
        <VRBtn key={n} name={`vr-tonk-hands-${n}`} label={String(n)} x={-0.17 + k * 0.12} y={-0.08} w={0.1} h={0.06}
          active={n === cur} disabled={busy} onPress={() => void tableAction(pid, 'configure', { options: { hands: n } })} />
      ))}
      <Text raycast={noRaycast} position={[0, -0.15, 0.004]} fontSize={0.014} color={colors.muted}>{`2–4 players · highest score after ${cur} hands wins`}</Text>
    </>;
  } else if (g && st.phase === 'over') {
    const res = g.winner === seat ? 'You win the game!' : g.winner === null ? 'Drawn game' : `${tonkName(st, g.winner)} wins the game`;
    body = <>
      <Text raycast={noRaycast} position={[0, 0.12, 0.004]} fontSize={0.026} color={colors.teal}>{res}</Text>
      <Text raycast={noRaycast} position={[0, 0.07, 0.004]} fontSize={0.017} color={colors.text} maxWidth={W - 0.06} textAlign="center">
        {g.active.map((i) => `${i === seat ? 'You' : tonkName(st, i)} ${g.scores[i]}`).join('  ·  ')}
      </Text>
      <VRBtn name="vr-tonk-rematch" label="Rematch" x={-0.19} y={-0.02} w={0.34} h={0.065} active disabled={busy} onPress={() => void tableAction(pid, 'rematch')} />
      <VRBtn name="vr-game-leaders-over" label="Leaderboard" x={0.19} y={-0.02} w={0.34} h={0.065} active={board} onPress={() => { setRules(false); setBoard((v) => !v); }} />
    </>;
  } else if (g && g.phase === 'hand_over' && g.lastHand) {
    const h = g.lastHand;
    body = <>
      <Text raycast={noRaycast} position={[0, 0.17, 0.004]} fontSize={0.021} color={colors.teal} maxWidth={W - 0.06} textAlign="center">{handLine(st, g, seat)}</Text>
      {g.active.map((i, k) => (
        <Text key={i} raycast={noRaycast} position={[-W / 2 + 0.05, 0.11 - k * 0.045, 0.004]} anchorX="left" fontSize={0.017} color={i === h.winner ? colors.teal : colors.text}>
          {`${i === seat ? 'You' : tonkName(st, i)}: ${(h.hands[i] ?? []).map(cardLabel).join(' ') || '—'}  = ${h.totals[i] ?? '–'}${h.points[i] ? `  (+${h.points[i]})` : ''}`}
        </Text>
      ))}
      <Text raycast={noRaycast} position={[0, -0.12, 0.004]} fontSize={0.015} color={colors.muted}>Next deal soon…</Text>
    </>;
  } else if (g) {
    const top = g.discard[g.discard.length - 1];
    const opp = g.active.filter((i) => i !== seat).map((i) => `${tonkName(st, i)} ${g.counts[i]}`).join(' · ');
    const status = drawPhase ? 'Your turn — draw a card, or drop'
      : playPhase ? (one && one === g.takenFromDiscard && hand.length > 1 ? "Can't throw back the card you just took"
        : sel.length === 0 ? 'Tap cards to select, then Spread / Hit / Discard'
        : one ? 'Discard it, or hit a spread'
        : canSpread(sel) ? 'Ready to spread' : 'Not a spread yet')
      : `${tonkName(st, g.turn)}'s turn`;
    const discardOk = !!one && !(one === g.takenFromDiscard && hand.length > 1);
    body = <>
      <Text raycast={noRaycast} position={[0, T - 0.085, 0.004]} fontSize={0.02} color={myTurn ? colors.teal : colors.text}>{status}</Text>
      <Text raycast={noRaycast} position={[0, T - 0.12, 0.004]} fontSize={0.014} color={colors.muted} maxWidth={W - 0.05} textAlign="center">
        {`Discard ${top ? cardLabel(top) : '—'} · Stock ${g.stockCount} · Your total ${total}  |  Cards: ${opp}`}
      </Text>
      {drawPhase && <>
        <VRBtn name="vr-tonk-draw-stock" label={`Draw (${g.stockCount})`} x={-0.27} y={0.07} w={0.25} h={0.07} disabled={busy || !g.stockCount}
          onPress={() => send({ type: 'draw', from: 'stock' })} />
        <VRBtn name="vr-tonk-take" label={top ? `Take ${cardLabel(top)}` : 'Discard empty'} x={0} y={0.07} w={0.25} h={0.07} disabled={busy || !top}
          onPress={() => send({ type: 'draw', from: 'discard' })} />
        <VRBtn name="vr-tonk-drop" label={`Drop (${total})`} x={0.27} y={0.07} w={0.25} h={0.07} disabled={busy}
          onPress={() => send({ type: 'drop' })} />
      </>}
      {playPhase && <>
        <VRBtn name="vr-tonk-spread" label="Spread" x={-0.3} y={0.07} w={0.18} h={0.065} disabled={busy || !canSpread(sel)} active={canSpread(sel)}
          onPress={() => send({ type: 'spread', cards: sel })} />
        <VRBtn name="vr-tonk-hit" label="Hit" x={-0.1} y={0.07} w={0.18} h={0.065} disabled={busy || !targets.length} active={targets.length > 0}
          onPress={() => one && targets[0] && send({ type: 'hit', card: one, spread: targets[0].id })} />
        <VRBtn name="vr-tonk-discard" label="Discard" x={0.1} y={0.07} w={0.18} h={0.065} disabled={busy || !discardOk}
          onPress={() => one && send({ type: 'discard', card: one })} />
        <VRBtn name="vr-tonk-clear" label="Clear" x={0.3} y={0.07} w={0.18} h={0.065} disabled={!sel.length} onPress={() => setSel([])} />
        {targets.length > 1 && targets.slice(0, 3).map((sp, k) => (
          <VRBtn key={sp.id} name={`vr-tonk-hit-${sp.id}`} label={`Hit ${spreadText(sp)}`} fontSize={0.013}
            x={(k - (Math.min(3, targets.length) - 1) / 2) * 0.27} y={-0.005} w={0.25} h={0.05}
            onPress={() => one && send({ type: 'hit', card: one, spread: sp.id })} />
        ))}
      </>}
    </>;
  }

  const showHand = !!g && st.phase === 'playing' && (g.phase === 'draw' || g.phase === 'play') && hand.length > 0;
  const perRow = 9;
  const rows: string[][] = [];
  for (let i = 0; i < hand.length; i += perRow) rows.push(hand.slice(i, i + perRow));

  return (
    <SeatPanelMount chair={def.chairs[seat]} height={H}>
      <Surface width={W} height={H} color={colors.panel} />
      {body}
      {showHand && rows.map((row, ri) => row.map((c, k) => (
        <VRCard key={c} card={c} x={-((row.length - 1) * (CW + 0.012)) / 2 + k * (CW + 0.012)} y={-T + 0.12 + (rows.length - 1 - ri) * (CH + 0.015)}
          enabled={playPhase && !busy} selected={sel.includes(c)} onPress={() => toggle(c)} />
      )))}
      <VRBtn label="Leave" x={W / 2 - 0.07} y={T - 0.035} w={0.11} h={0.045} fontSize={0.015} onPress={() => leaveTable(pid)} />
      <VRBtn name="vr-game-howto" label="?" x={W / 2 - 0.16} y={T - 0.035} w={0.05} h={0.045} fontSize={0.017} active={rules} onPress={() => { setBoard(false); setRules((v) => !v); }} />
      <VRBtn name="vr-game-leaders" label="Leaders" x={W / 2 - 0.26} y={T - 0.035} w={0.13} h={0.045} fontSize={0.015} active={board} onPress={() => { setRules(false); setBoard((v) => !v); }} />
      {g && st.phase !== 'lobby' && (
        <Text raycast={noRaycast} position={[-W / 2 + 0.02, T - 0.035, 0.004]} anchorX="left" fontSize={0.015} color={colors.muted} maxWidth={0.48}>
          {`Hand ${g.handNo}/${g.handsToPlay} · ${g.active.map((i) => `${i === seat ? 'You' : tonkName(st, i).slice(0, 8)} ${g.scores[i]}`).join(' · ')}`}
        </Text>
      )}
      {st.phase === 'lobby' && <Text raycast={noRaycast} position={[-W / 2 + 0.02, T - 0.035, 0.004]} anchorX="left" fontSize={0.02} color={colors.teal}>{`${def.glyph} ${def.label}`}</Text>}
      {rules && <group position={[0, 0, 0.02]}><VRRulesPanel def={def} y={-0.31} onClose={() => setRules(false)} /></group>}
      {board && <group position={[0, 0, 0.02]}><VRLeaderboardPanel def={def} worldId={worldId} refreshKey={st.gameId} onClose={() => setBoard(false)} /></group>}
      {entry.error && <Text raycast={noRaycast} position={[0, -T + 0.02, 0.004]} fontSize={0.013} color="#ff9b9b">{entry.error}</Text>}
    </SeatPanelMount>
  );
}

export default TonkVRPanel;
